import { useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Activity,
  AlertCircle,
  Award,
  Brain,
  Briefcase,
  Calendar,
  CheckCircle,
  CheckCircle2,
  Clock3,
  CreditCard,
  DollarSign,
  Gauge,
  Globe,
  GraduationCap,
  Laptop,
  Mail,
  MapPin,
  Monitor,
  Network,
  Phone,
  Send,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  User as UserIcon,
  UserCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format, formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type JourneyProfile = Record<string, any>;

interface UserProfileIntelligenceProps {
  profile: JourneyProfile;
  loading?: boolean;
  answers?: any[];
  competitions?: any[];
  loginHistory?: any[];
}

const clamp = (value: number, min = 0, max = 100) =>
  Math.max(min, Math.min(max, value));

const clamp01 = (value: number) => clamp(value, 0, 1);

const toNumber = (value: unknown, fallback = 0) =>
  typeof value === "number" && !isNaN(value) ? value : fallback;

const formatDateSafe = (value?: string | Date | null, withTime = false) => {
  if (!value) return "Unknown";
  try {
    return format(new Date(value), withTime ? "PPpp" : "PPP");
  } catch {
    return String(value);
  }
};

const getInitials = (first?: string, last?: string) => {
  const parts = [first, last].filter(Boolean) as string[];
  if (parts.length === 0) return "PR";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

type DerivedSegment = {
  label: string;
  active: boolean;
  confidence?: number;
};

const deriveSegments = (
  profile: JourneyProfile,
  explicitSegments?: DerivedSegment[] | any[],
): DerivedSegment[] => {
  if (Array.isArray(explicitSegments) && explicitSegments.length > 0) {
    return explicitSegments.map((segment: any) => {
      if (typeof segment === "string") {
        return { label: segment, active: true };
      }
      return {
        label: segment.label ?? "Segment",
        active: segment.active !== false,
        confidence:
          typeof segment.confidence === "number"
            ? segment.confidence
            : undefined,
      };
    });
  }

  if (Array.isArray(profile?.behavioralSegments) && profile.behavioralSegments.length > 0) {
    return profile.behavioralSegments.map((segment: any) =>
      typeof segment === "string"
        ? { label: segment, active: true }
        : {
            label: segment.label ?? "Segment",
            active: segment.active !== false,
          },
    );
  }

  const interestString =
    (typeof profile?.interests === "string" && profile.interests) || "";
  const interestArray: string[] = Array.isArray(profile?.interests)
    ? profile.interests
    : interestString
        .split(/[|,]/)
        .map((item) => item.trim())
        .filter(Boolean);

  return interestArray.slice(0, 8).map((label) => ({
    label,
    active: true,
  }));
};

function UserEmailQuotaSection({ profile }: { profile: JourneyProfile }) {
  const userId =
    typeof profile?.id === "number" && !isNaN(profile.id) ? profile.id : null;

  const { data, isLoading, refetch } = useQuery<{
    userId: number;
    maxPerWeek: number;
    emailCount: number;
    weekStart: string | null;
  }>({
    queryKey: userId ? ["/api/admin/users", userId, "email-quota"] : ["no-user"],
    enabled: !!userId,
    retry: false,
    queryFn: async () => {
      if (!userId) {
        return { userId: 0, maxPerWeek: 4, emailCount: 0, weekStart: null };
      }
      const response = await apiRequest(
        "GET",
        `/api/admin/users/${userId}/email-quota`
      );
      return response.json();
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      if (!userId) return;
      await apiRequest(
        "POST",
        `/api/admin/users/${userId}/email-quota/reset`
      );
    },
    onSuccess: () => {
      refetch();
    },
  });

  const maxPerWeek = data?.maxPerWeek ?? 4;
  const used = data?.emailCount ?? 0;
  const remaining = Math.max(0, maxPerWeek - used);
  const percentage = maxPerWeek > 0 ? (used / maxPerWeek) * 100 : 0;

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg flex items-center gap-2">
          <Send className="h-4 w-4" />
          Weekly Email Limit
        </CardTitle>
        <CardDescription>
          Per-user marketing email limit and reset controls
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {isLoading || !userId ? (
          <div className="text-muted-foreground">Loading quota...</div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase text-muted-foreground">
                  Emails used this week
                </div>
                <div className="text-lg font-semibold">
                  {used} / {maxPerWeek}
                </div>
              </div>
              <Badge
                className={cn(
                  "text-xs",
                  remaining > 0
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-red-100 text-red-800"
                )}
              >
                {remaining > 0
                  ? `${remaining} remaining`
                  : "Limit reached (4 / week)"}
              </Badge>
            </div>
            <Progress value={percentage} className="h-2" />
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                {data?.weekStart
                  ? `Current week from ${formatDateSafe(
                      data.weekStart,
                      true
                    )}`
                  : "No quota row yet – user has not received marketing emails this week."}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => resetMutation.mutate()}
                disabled={resetMutation.isPending || !userId}
              >
                {resetMutation.isPending ? "Resetting..." : "Reset quota"}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function useUserSegments(profile: JourneyProfile) {
  const userId =
    typeof profile?.id === "number" && !isNaN(profile.id) ? profile.id : null;

  const { data } = useQuery<{
    segments: Array<{
      segmentId: number;
      segmentName: string;
      segmentDescription: string | null;
      confidence: string | number | null;
      assignedAt: string;
    }>;
    lastCalculated: string | null;
    totalAnswers: number | null;
  }>({
    queryKey: userId
      ? ["/api/users", userId, "segments"]
      : ["no-user-segments"],
    enabled: !!userId,
    retry: false,
    queryFn: async () => {
      if (!userId) {
        return {
          segments: [],
          lastCalculated: null,
          totalAnswers: null,
        };
      }
      const response = await apiRequest(
        "GET",
        `/api/v1/segments/users/${userId}/profile`,
      );
      const json = await response.json();

      const segments = Array.isArray(json?.profile?.segments)
        ? json.profile.segments
        : [];

      const mappedSegments = segments.map((seg: any) => ({
        segmentId: seg.segment?.id ?? seg.segmentId ?? 0,
        segmentName: seg.segment?.name ?? seg.segmentName ?? "Segment",
        segmentDescription:
          seg.segment?.description ?? seg.segmentDescription ?? null,
        confidence: seg.confidence ?? null,
        assignedAt:
          seg.assignedAt ??
          seg.segment?.assignedAt ??
          new Date().toISOString(),
      }));

      return {
        segments: mappedSegments,
        lastCalculated: json?.profile?.lastCalculated ?? null,
        totalAnswers:
          typeof json?.profile?.totalAnswers === "number"
            ? json.profile.totalAnswers
            : null,
      };
    },
  });

  const derived = useMemo<DerivedSegment[]>(() => {
    if (!data || !data.segments || data.segments.length === 0) return [];
    return data.segments.map((seg) => ({
      label: seg.segmentName || seg.segmentDescription || "Segment",
      active: true,
      confidence:
        typeof seg.confidence === "number"
          ? seg.confidence
          : seg.confidence != null
          ? Number(seg.confidence)
          : undefined,
    }));
  }, [data]);

  return {
    segments: derived,
    lastCalculated: data?.lastCalculated ?? null,
    totalAnswers: data?.totalAnswers ?? null,
  };
}

const deriveContentAffinity = (
  profile: JourneyProfile,
  segments: DerivedSegment[],
) => {
  if (profile?.contentAffinity) {
    return profile.contentAffinity;
  }

  const interests = segments.filter((segment) => segment.active).map((s) => s.label);

  return {
    preferredTypes: profile?.contentPreferenceTypes ||
      (Array.isArray(profile?.contentTypes) ? profile.contentTypes : interests.slice(0, 3)),
    topics: interests.slice(0, 6),
    engagementType:
      profile?.engagementType ||
      (interests.some((item) => /share|social|refer/i.test(item)) ? "Advocate" : "Explorer"),
  };
};

const deriveDeviceInsights = (
  profile: JourneyProfile,
  loginHistory: any[],
) => {
  const counts = {
    Mobile: 0,
    Desktop: 0,
    Tablet: 0,
    Other: 0,
  };

  const normalizeDevice = (device?: string | null) => {
    if (!device) return "Other";
    const normalized = device.toLowerCase();
    if (/(mobile|iphone|android|phone)/.test(normalized)) return "Mobile";
    if (/(tablet|ipad)/.test(normalized)) return "Tablet";
    if (/(desktop|windows|mac|linux)/.test(normalized)) return "Desktop";
    return "Other";
  };

  loginHistory.forEach((entry) => {
    counts[normalizeDevice(entry?.device)] += 1;
  });

  if (
    counts.Mobile === 0 &&
    counts.Desktop === 0 &&
    counts.Tablet === 0 &&
    counts.Other === 0 &&
    profile?.lastLoginDevice
  ) {
    counts[normalizeDevice(profile.lastLoginDevice)] = 1;
  }

  const total = Object.values(counts).reduce((sum, value) => sum + value, 0);

  const distribution =
    total === 0
      ? [
          { label: "Mobile", value: 0 },
          { label: "Desktop", value: 0 },
          { label: "Tablet", value: 0 },
        ]
      : (Object.entries(counts).map(([label, value]) => ({
          label,
          value: Math.round((value / total) * 100),
        })) as Array<{ label: string; value: number }>);

  const preferredTime = (() => {
    const hours = loginHistory
      .map((entry) => {
        const timestamp = entry?.timestamp;
        if (!timestamp) return null;
        return new Date(timestamp).getHours();
      })
      .filter((hour) => hour !== null) as number[];

    if (hours.length === 0) return profile?.preferredLoginWindow || "Not enough data";

    const evening = hours.filter((hour) => hour >= 18 && hour < 24).length;
    const afternoon = hours.filter((hour) => hour >= 12 && hour < 18).length;
    const morning = hours.filter((hour) => hour >= 6 && hour < 12).length;
    const night = hours.filter((hour) => hour < 6).length;

    const max = Math.max(evening, afternoon, morning, night);
    if (max === evening) return "Evening (6-10 PM)";
    if (max === afternoon) return "Afternoon (12-6 PM)";
    if (max === morning) return "Morning (6-12 PM)";
    return "Late Night (12-6 AM)";
  })();

  const topDevice =
    distribution.reduce(
      (highest, current) => (current.value > highest.value ? current : highest),
      { label: "Unknown", value: 0 },
    ).label || profile?.lastLoginDevice || "Unknown";

  return {
    deviceType: topDevice,
    os: profile?.lastLoginOS || loginHistory[0]?.os || "Unknown",
    browser: profile?.lastLoginBrowser || loginHistory[0]?.browser || "Unknown",
    preferredTime,
    distribution,
  };
};

const deriveEngagementStats = (
  profile: JourneyProfile,
  answers: any[],
  competitions: any[],
  loginHistory: any[],
) => {
  const totalEntries = toNumber(
    profile?.totalEntries,
    competitions.reduce(
      (sum, comp) => sum + toNumber(comp.userEntries || comp.totalEntries, 0),
      0,
    ),
  );

  const answersCount = answers.length;
  const competitionsCount = competitions.length;
  const loginCount = loginHistory.length;

  const retentionScore = clamp(
    totalEntries * 2 + competitionsCount * 8 + answersCount * 3,
  );

  const engagementLevel =
    retentionScore >= 75
      ? "High Engagement"
      : retentionScore >= 45
        ? "Moderate Engagement"
        : "Low Engagement";

  const lifecycleStage = profile?.isActive
    ? retentionScore >= 45
      ? "Active"
      : "Re-engage"
    : "Dormant";

  const lastActivity = loginHistory[0]?.timestamp
    ? `${formatDistanceToNow(new Date(loginHistory[0].timestamp), { addSuffix: true })}`
    : "No recent activity";

  const sessionFrequency =
    loginCount > 24
      ? "Multiple times daily"
      : loginCount > 12
        ? "Daily"
        : loginCount > 6
          ? "Weekly"
          : loginCount > 2
            ? "Monthly"
            : "Sporadic";

  const avgSessionDuration = profile?.averageSessionDuration
    ? `${profile.averageSessionDuration} minutes`
    : `${Math.max(5, Math.round((totalEntries || answersCount || 1) / Math.max(loginCount || 1, 1)) * 5)} minutes`;

  return {
    level: engagementLevel,
    lifecycle: lifecycleStage,
    lastActivity,
    frequency: sessionFrequency,
    duration: avgSessionDuration,
    retention: retentionScore,
  };
};

const deriveFinancialStats = (
  profile: JourneyProfile,
  competitions: any[],
  answers: any[],
) => {
  const totalEntries = toNumber(
    profile?.totalEntries,
    competitions.reduce(
      (sum, comp) => sum + toNumber(comp.userEntries || comp.totalEntries, 0),
      0,
    ),
  );

  const answersCount = answers.length;

  const score = clamp(totalEntries * 4 + competitions.length * 6 + answersCount * 2);

  const tier =
    score >= 75 ? "Premium" : score >= 45 ? "Growth" : score > 0 ? "Emerging" : "Unknown";

  const avgTransaction =
    profile?.averageTransactionValue ??
    profile?.avgTransaction ??
    (profile?.lifetimeValue && totalEntries
      ? Math.round((profile.lifetimeValue / totalEntries) * 100) / 100
      : totalEntries
        ? Math.round((25 + totalEntries * 1.35) * 100) / 100
        : null);

  const paymentMethod =
    profile?.preferredPaymentMethod ||
    profile?.paymentMethod ||
    profile?.lastPaymentMethod ||
    "Not captured";

  const frequency =
    totalEntries >= 60
      ? "Daily"
      : totalEntries >= 30
        ? "2-3 times per week"
        : totalEntries >= 12
          ? "Weekly"
          : totalEntries > 0
            ? "Monthly"
            : "No purchases recorded";

  return {
    tier: { label: tier, value: score },
    paymentMethod,
    avgTransaction,
    frequency,
  };
};

const deriveIntentScores = (
  profile: JourneyProfile,
  segments: DerivedSegment[],
  answers: any[],
  competitions: any[],
) => {
  const referrals = Array.isArray(profile?.referrals)
    ? profile.referrals.length
    : toNumber(profile?.totalReferrals, 0);
  const totalShares = toNumber(profile?.totalShares, 0);

  const dealScore = clamp(
    (profile?.marketingConsent ? 20 : 0) +
      totalShares * 4 +
      referrals * 3 +
      (profile?.hasCouponUsage ? 15 : 0),
  );

  const studySignals =
    segments.filter((segment) =>
      /study|education|learn|career/i.test(segment.label),
    ).length +
    answers.filter((answer: any) =>
      /career|study|education/i.test(answer?.questionText || ""),
    ).length;

  const studyScore = clamp(studySignals * 10 + (profile?.education ? 20 : 0));

  const globalSignals =
    competitions.filter((competition: any) =>
      /international|global|world/i.test(competition?.title || ""),
    ).length +
    segments.filter((segment) =>
      /global|travel|international/i.test(segment.label),
    ).length;

  const globalScore = clamp(globalSignals * 15 + (profile?.location?.includes("USA") ? 15 : 0));

  return [
    { label: "Looking for Deals", value: dealScore },
    { label: "Career/Study Intent", value: studyScore },
    { label: "Global Preference", value: globalScore },
  ];
};

const deriveSocialStats = (
  profile: JourneyProfile,
  competitions: any[],
) => {
  const totalReferrals = Array.isArray(profile?.referrals)
    ? profile.referrals.length
    : toNumber(profile?.totalReferrals, 0);

  const totalEntries = toNumber(
    profile?.totalEntries,
    competitions.reduce(
      (sum, comp) => sum + toNumber(comp.userEntries || comp.totalEntries, 0),
      0,
    ),
  );

  const followers =
    profile?.followers ??
    profile?.socialFollowers ??
    Math.max(totalReferrals * 40, 0);

  const conversionRate = totalReferrals
    ? clamp(Math.round((totalEntries / Math.max(totalReferrals, 1)) * 8))
    : 0;

  const influenceScore = clamp(conversionRate * 0.7 + totalReferrals * 3);

  const influenceTier =
    followers >= 20000
      ? "High"
      : followers >= 5000
        ? "Mid"
        : followers > 0
          ? "Emerging"
          : "No reach";

  return {
    referrals: totalReferrals,
    conversionRate,
    followers,
    influenceTier,
    influenceScore,
  };
};

const deriveSentiment = (
  profile: JourneyProfile,
  answers: any[],
) => {
  const sentimentScoreRaw =
    profile?.sentimentScore ??
    profile?.feedbackScore ??
    profile?.npsScore ??
    null;

  const score =
    typeof sentimentScoreRaw === "number"
      ? clamp(sentimentScoreRaw, 0, 10)
      : clamp(Math.round(Math.min(10, answers.length / 2 + (profile?.marketingConsent ? 2 : 0))), 0, 10);

  const summary =
    profile?.sentimentSummary ||
    (score >= 8
      ? "Highly satisfied and engaged with platform content and offers."
      : score >= 6
        ? "Shows positive interest with occasional engagement spikes."
        : "Low sentiment indicators. Consider targeted re-engagement.");

  return { score, summary };
};

const deriveAIInsights = (
  profile: JourneyProfile,
  competitions: any[],
  intentScores: { label: string; value: number }[],
) => {
  if (profile?.aiInsights) {
    return {
      propensityToBet: clamp01(profile.aiInsights.propensityToBet ?? 0),
      propensityToStudy: clamp01(profile.aiInsights.propensityToStudy ?? 0),
      clusterId: profile.aiInsights.clusterId ?? "Unclustered",
      engagementScore: clamp(
        profile.aiInsights.engagementScore ?? profile?.engagementScore ?? 0,
      ),
    };
  }

  const totalEntries = toNumber(
    profile?.totalEntries,
    competitions.reduce(
      (sum, comp) => sum + toNumber(comp.userEntries || comp.totalEntries, 0),
      0,
    ),
  );

  const propensityToBet = clamp01(totalEntries * 0.02 + competitions.length * 0.05);

  const careerIntent = intentScores.find(
    (intent) => intent.label === "Career/Study Intent",
  )?.value;

  const propensityToStudy = clamp01((careerIntent ?? 0) / 100 + (profile?.education ? 0.2 : 0));

  const engagementScore = clamp(
    toNumber(profile?.engagementScore, 0) ||
      totalEntries * 2 +
        competitions.length * 7 +
        (Array.isArray(profile?.answers) ? profile.answers.length : 0) * 3,
  );

  const clusterId = (() => {
    if (propensityToStudy > 0.6) return "Learning Explorer";
    if (propensityToBet > 0.6) return "Competition Specialist";
    if (engagementScore > 80) return "Loyal Advocate";
    return "Growth Potential";
  })();

  return {
    propensityToBet,
    propensityToStudy,
    clusterId,
    engagementScore,
  };
};

const deriveDataSources = (
  profile: JourneyProfile,
  competitions: any[],
  answers: any[],
  loginHistory: any[],
) => {
  if (Array.isArray(profile?.dataSources) && profile.dataSources.length > 0) {
    return profile.dataSources;
  }

  return [
    {
      source: "Competition Activity",
      type: "Behavioral",
      frequency: competitions.length ? "Real-time" : "No activity recorded",
    },
    {
      source: "Answer History",
      type: "Engagement",
      frequency: answers.length ? "Per response" : "No answers collected",
    },
    {
      source: "Referrals & Sharing",
      type: "Social",
      frequency:
        Array.isArray(profile?.referrals) && profile.referrals.length
          ? "Weekly"
          : "No referrals recorded",
    },
    {
      source: "Device Signals",
      type: "Platform",
      frequency: loginHistory.length ? "Per session" : "No sessions recorded",
    },
  ];
};

const formatDistanceOrDate = (value?: string | null) => {
  if (!value) return "Unknown";
  try {
    const date = new Date(value);
    return `${formatDistanceToNow(date, { addSuffix: true })}`;
  } catch {
    return String(value);
  }
};

function MiniStat({
  icon: Icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value: string | number;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 text-slate-400 mt-1" />
      <div>
        <p className="text-xs uppercase text-slate-400">{label}</p>
        <p className="text-sm font-medium text-slate-700">{value ?? "—"}</p>
      </div>
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  children,
}: {
  icon: any;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-1">
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div>
        <p className="text-xs uppercase text-slate-400">{label}</p>
        <p className="text-sm text-slate-700">{children}</p>
      </div>
    </div>
  );
}

function ComplianceBadge({
  label,
  status,
}: {
  label: string;
  status: string;
}) {
  const normalized = status.toLowerCase();
  const isVerified =
    normalized.includes("verified") ||
    normalized.includes("active") ||
    normalized.includes("yes");

  return (
    <div className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2">
      <ShieldCheck
        className={cn(
          "h-4 w-4",
          isVerified ? "text-emerald-500" : "text-amber-500",
        )}
      />
      <div>
        <p className="text-xs uppercase text-slate-400">{label}</p>
        <p
          className={cn(
            "text-sm font-medium",
            isVerified ? "text-emerald-600" : "text-amber-600",
          )}
        >
          {status}
        </p>
      </div>
    </div>
  );
}

function CircularMetric({
  label,
  value,
  color = "#0F172A",
  secondaryText,
}: {
  label: string;
  value: number;
  color?: string;
  secondaryText?: string;
}) {
  const percentage = clamp(value);
  const style = {
    background: `conic-gradient(${color} ${percentage * 3.6}deg, rgba(15,23,42,0.1) ${percentage * 3.6}deg)`,
  } as const;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative h-20 w-20">
        <div
          className="absolute inset-0 rounded-full"
          style={style}
        ></div>
        <div className="absolute inset-1 rounded-full bg-white flex items-center justify-center border border-slate-100">
          <span className="text-lg font-semibold text-slate-900">
            {percentage}%
          </span>
        </div>
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        {secondaryText && (
          <p className="text-xs text-slate-500">{secondaryText}</p>
        )}
      </div>
    </div>
  );
}

export default function UserProfileIntelligence({
  profile,
  loading,
  answers: answersProp,
  competitions: competitionsProp,
  loginHistory: loginHistoryProp,
}: UserProfileIntelligenceProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const answers = useMemo(() => {
    if (Array.isArray(answersProp)) return answersProp;
    if (Array.isArray(profile?.answers)) return profile.answers;
    return [];
  }, [answersProp, profile]);

  const competitions = useMemo(() => {
    if (Array.isArray(competitionsProp)) return competitionsProp;
    if (Array.isArray(profile?.competitions)) return profile.competitions;
    return [];
  }, [competitionsProp, profile]);

  const loginHistory = useMemo(() => {
    if (Array.isArray(loginHistoryProp)) return loginHistoryProp;
    if (Array.isArray(profile?.loginHistory)) return profile.loginHistory;
    return [];
  }, [loginHistoryProp, profile]);

  const userId =
    typeof profile?.id === "number" && !isNaN(profile.id) ? profile.id : null;

  const analyzeUserMutation = useMutation({
    mutationFn: async () => {
      if (!userId) {
        throw new Error("User ID is missing for analysis");
      }
      const response = await apiRequest("POST", "/api/analyze/user", {
        targetUserId: userId,
      });
      return response.json();
    },
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: ["/api/users", userId, "segments"],
        });
      }
      toast({
        title: "AI analysis complete",
        description: "User profile insights have been refreshed.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "AI analysis failed",
        description:
          error?.message ||
          "Unable to analyze this user right now. Please check AI configuration and try again.",
        variant: "destructive",
      });
    },
  });

  const {
    segments: assignedSegments,
    lastCalculated: segmentsLastCalculated,
  } = useUserSegments(profile);

  const segments = useMemo(
    () => deriveSegments(profile, assignedSegments.length ? assignedSegments : profile?.segments),
    [profile, assignedSegments],
  );

  const contentAffinity = useMemo(
    () => deriveContentAffinity(profile, segments),
    [profile, segments],
  );

  const deviceInsights = useMemo(
    () => deriveDeviceInsights(profile, loginHistory),
    [profile, loginHistory],
  );

  const engagementStats = useMemo(
    () => deriveEngagementStats(profile, answers, competitions, loginHistory),
    [profile, answers, competitions, loginHistory],
  );

  const financialStats = useMemo(
    () => deriveFinancialStats(profile, competitions, answers),
    [profile, competitions, answers],
  );

  const intentScores = useMemo(
    () => deriveIntentScores(profile, segments, answers, competitions),
    [profile, segments, answers, competitions],
  );

  const socialStats = useMemo(
    () => deriveSocialStats(profile, competitions),
    [profile, competitions],
  );

  const sentiment = useMemo(
    () => deriveSentiment(profile, answers),
    [profile, answers],
  );

  const aiInsights = useMemo(
    () => deriveAIInsights(profile, competitions, intentScores),
    [profile, competitions, intentScores],
  );

  const dataSources = useMemo(
    () => deriveDataSources(profile, competitions, answers, loginHistory),
    [profile, competitions, answers, loginHistory],
  );

  const formattedLastAiUpdate = useMemo(() => {
    if (!segmentsLastCalculated) return null;
    try {
      const date = new Date(segmentsLastCalculated);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return null;
    }
  }, [segmentsLastCalculated]);

  const age = useMemo(() => {
    if (!profile?.dateOfBirth && !profile?.dob) return null;
    try {
      const date = new Date(profile.dateOfBirth || profile.dob);
      const diff = Date.now() - date.getTime();
      const ageDate = new Date(diff);
      return Math.abs(ageDate.getUTCFullYear() - 1970);
    } catch {
      return null;
    }
  }, [profile]);

  const location =
    profile?.location ||
    [profile?.city, profile?.state, profile?.country]
      .filter(Boolean)
      .join(", ") ||
    "Location unavailable";

  const verificationStatus =
    profile?.isVerified ??
    profile?.ageVerified ??
    profile?.idVerified ??
    profile?.isActive;

  const aiUserAnalysis: any = analyzeUserMutation.data;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-60" />
            <div className="h-5 bg-slate-200 rounded w-80" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx} className="h-40 bg-slate-200 rounded" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <Card className="shadow-none border-none bg-transparent">
        <CardContent className="px-0 pt-0">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div>
              <h2 className="text-3xl font-bold text-slate-900">
                User Profile Intelligence
              </h2>
              <p className="text-slate-500">
                Comprehensive behavioral and demographic analytics
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => analyzeUserMutation.mutate()}
                disabled={analyzeUserMutation.isPending || !userId}
                className="flex items-center gap-2"
              >
                {analyzeUserMutation.isPending ? (
                  <span className="h-4 w-4 rounded-full border-2 border-b-transparent border-slate-500 animate-spin" />
                ) : (
                  <Brain className="h-4 w-4" />
                )}
                <span className="text-xs sm:text-sm">Analyze with AI</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                {profile?.profileImageUrl ? (
                  <AvatarImage
                    src={profile.profileImageUrl}
                    alt={`${profile.firstName ?? ""} ${profile.lastName ?? ""}`}
                  />
                ) : (
                  <AvatarFallback className="bg-blue-100 text-blue-800">
                    {getInitials(profile?.firstName, profile?.lastName)}
                  </AvatarFallback>
                )}
              </Avatar>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-xl font-semibold text-slate-900">
                    {profile?.firstName} {profile?.lastName}
                  </h3>
                  {verificationStatus && (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Active
                    </Badge>
                  )}
                  {profile?.isAdmin && (
                    <Badge className="bg-blue-100 text-blue-700">
                      Admin
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600 mt-1">
                  {age && (
                    <>
                      <span>{age} years old</span>
                      <span className="text-slate-300">•</span>
                    </>
                  )}
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3 text-slate-400" />
                    {location}
                  </span>
                  {profile?.status && (
                    <>
                      <span className="text-slate-300">•</span>
                      <span>{profile.status}</span>
                    </>
                  )}
                </div>
                <div className="text-xs text-slate-400 mt-2">
                  User ID: {profile?.id} • Username: @{profile?.username}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="outline" className="rounded-full px-3">
                <UserCheck className="h-3 w-3 mr-1" />
                {profile?.role ? profile.role.toUpperCase() : "USER"}
              </Badge>
              <Badge variant="outline" className="rounded-full px-3 flex items-center gap-1">
                <Activity className="h-3 w-3" />
                {engagementStats.lastActivity}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="space-y-5">
        <TabsList className="w-full overflow-x-auto flex flex-wrap gap-2 bg-white border border-slate-200">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="overview">Overview</TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                High-level snapshot of identity, segmentation, devices, and engagement.
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="email">Email Quota</TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                Weekly marketing email cap and quota controls for this user.
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="activity">Activity</TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                Competition participation, engagement streaks, and login history.
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="financial">Financial</TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                Spend, prize redemptions, and value-related behaviour.
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="social">Social</TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                Referrals, social reach, and influence indicators.
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="insights">Insights</TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                Manual intent, sentiment, and feedback-driven insights.
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="ai">AI &amp; Trust</TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                AI-derived traits, propensities, and trust/safety assessments.
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="data">Data</TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                Data sources, freshness, and coverage for this profile.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </TabsList>

        <TabsContent value="overview" className="space-y-5">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <Card className="shadow-sm xl:col-span-2">
              <CardHeader className="pb-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex flex-col gap-1 cursor-help">
                        <CardTitle className="text-lg">
                          Core User Information
                        </CardTitle>
                        <CardDescription>
                          Verified personal details and identification markers
                        </CardDescription>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      Basic identity, contact, and demographic fields used for
                      personalization and compliance checks.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <InfoRow icon={UserIcon} label="Full Name">
                  {profile?.firstName} {profile?.lastName}
                </InfoRow>
                <InfoRow icon={Mail} label="Email">
                  {profile?.email || "Not provided"}
                </InfoRow>
                <InfoRow icon={Phone} label="Phone">
                  {profile?.phone || "Not provided"}
                </InfoRow>
                <InfoRow icon={UserCheck} label="Gender">
                  {profile?.gender || "Not specified"}
                </InfoRow>
                <InfoRow icon={Calendar} label="Date of Birth">
                  {formatDateSafe(profile?.dateOfBirth || profile?.dob)}
                </InfoRow>
                <InfoRow icon={MapPin} label="Location">
                  {location}
                </InfoRow>
                <InfoRow icon={Briefcase} label="Occupation">
                  {profile?.occupation || "Unknown"}
                </InfoRow>
                <InfoRow icon={GraduationCap} label="Education">
                  {profile?.education || "Unknown"}
                </InfoRow>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex flex-col gap-1 cursor-help">
                        <CardTitle className="text-lg">
                          Behavioral Segmentation
                        </CardTitle>
                        <CardDescription>
                          Real-time segmentation tags derived from user behaviour
                        </CardDescription>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      Shows which behavioural segments this user belongs to and
                      how confidently they match each segment.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {segments.length ? (
                  segments.map((segment, idx) => (
                    <span
                      key={idx}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 border transition-colors",
                        segment.active
                          ? "bg-blue-600 text-white border-blue-700"
                          : "bg-slate-100 text-slate-500 border-transparent",
                      )}
                    >
                      {segment.active ? (
                        <CheckCircle2 className="h-3 w-3" />
                      ) : (
                        <AlertCircle className="h-3 w-3" />
                      )}
                      <span>{segment.label}</span>
                      {typeof segment.confidence === "number" && (
                        <span className="text-[10px] opacity-80">
                          {Math.round(segment.confidence * 100)}%
                        </span>
                      )}
                    </span>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">
                    N/A – no segmentation data available for this user.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <Card className="shadow-sm">
              <CardHeader>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex flex-col gap-1 cursor-help">
                        <CardTitle>Device &amp; Platform Insights</CardTitle>
                        <CardDescription>
                          Preferred access patterns across devices and operating systems
                        </CardDescription>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      Highlights which devices and platforms the user most often
                      uses, plus their typical time of day to log in.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <MiniStat
                    icon={Smartphone}
                    label="Device Type"
                    value={deviceInsights.deviceType}
                  />
                  <MiniStat
                    icon={Monitor}
                    label="Operating System"
                    value={deviceInsights.os}
                  />
                  <MiniStat
                    icon={Globe}
                    label="Browser"
                    value={deviceInsights.browser}
                  />
                  <MiniStat
                    icon={Clock3}
                    label="Preferred Time"
                    value={deviceInsights.preferredTime}
                  />
                </div>
                <div className="space-y-4">
                  {deviceInsights.distribution.map((item, idx) => (
                    <div key={idx}>
                      <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span>{item.label}</span>
                        <span>{item.value}%</span>
                      </div>
                      <Progress value={item.value} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex flex-col gap-1 cursor-help">
                        <CardTitle>Engagement &amp; Lifecycle</CardTitle>
                        <CardDescription>
                          Real-time engagement trajectories and lifecycle signals
                        </CardDescription>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      Summarises how frequently this user engages, how long they
                      stay active, and their retention health.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-indigo-100 text-indigo-600">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    {engagementStats.level}
                  </Badge>
                  <Badge className="bg-green-100 text-green-600">
                    <Activity className="h-3 w-3 mr-1" />
                    {engagementStats.lifecycle}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <MiniStat
                    icon={Clock3}
                    label="Last Activity"
                    value={engagementStats.lastActivity}
                  />
                  <MiniStat
                    icon={Target}
                    label="Session Frequency"
                    value={engagementStats.frequency}
                  />
                  <MiniStat
                    icon={Gauge}
                    label="Avg Session Duration"
                    value={engagementStats.duration}
                  />
                </div>
                <div>
                  <div className="flex justify-between text-sm text-slate-700 mb-1">
                    <span>Retention Score</span>
                    <span>{engagementStats.retention}%</span>
                  </div>
                  <Progress value={engagementStats.retention} className="h-2" />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="email" className="space-y-5">
          <UserEmailQuotaSection profile={profile} />
        </TabsContent>

        <TabsContent value="activity" className="space-y-5">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Recent Sessions</CardTitle>
              <CardDescription>
                Detailed login history with location and device context
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loginHistory.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">
                          Date &amp; Time
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          IP Address
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          Location
                        </th>
                        <th className="px-3 py-2 text-left font-medium">Device</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {loginHistory.slice(0, 12).map((entry: any, idx: number) => (
                        <tr key={idx} className="bg-white">
                          <td className="px-3 py-2">
                            {entry?.timestamp
                              ? formatDateSafe(entry.timestamp, true)
                              : "Unknown"}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {entry?.ip || "N/A"}
                          </td>
                          <td className="px-3 py-2 text-sm">
                            {entry?.location || "Unknown"}
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className="text-xs">
                              {entry?.device || "Unknown"}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm text-slate-500">
                  No login history recorded for this user.
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Competition Journey</CardTitle>
                <CardDescription>
                  Participation summary across recent competitions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {competitions.length ? (
                  competitions.slice(0, 6).map((competition: any, idx: number) => (
                    <div
                      key={idx}
                      className="border border-slate-100 rounded-lg px-4 py-3 flex flex-col gap-1"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-900">
                          {competition?.title || `Competition #${competition?.id}`}
                        </p>
                        <Badge variant="outline">
                          {formatDateSafe(competition?.createdAt || competition?.endDate)}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-slate-600">
                        <span className="flex items-center gap-1">
                          <Send className="h-3 w-3" />
                          Entries: {toNumber(competition?.userEntries, 0)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Target className="h-3 w-3" />
                          Answers: {toNumber(competition?.userAnswers, 0)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Award className="h-3 w-3" />
                          {competition?.isActive ? "Active" : "Completed"}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">
                    No competitions associated with this user.
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Answer Highlights</CardTitle>
                <CardDescription>
                  Most recent question responses submitted by the user
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {answers.length ? (
                  answers.slice(0, 6).map((answer: any, idx: number) => (
                    <div
                      key={idx}
                      className="border border-slate-100 rounded-lg px-4 py-3"
                    >
                      <p className="text-sm font-medium text-slate-900">
                        {answer?.questionText || "Question"}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Answered {formatDistanceOrDate(answer?.createdAt)}
                      </p>
                      <div className="mt-2 text-sm text-slate-600">
                        <span className="font-medium">Response:</span>{" "}
                        {answer?.answerText || "No answer recorded"}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">
                    No answers recorded for this user.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="financial" className="space-y-5">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Financial &amp; Spending Behaviour</CardTitle>
              <CardDescription>
                Transactional tendencies across key spending dimensions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm font-medium text-slate-700 mb-2">
                  <span>Spending Tier</span>
                  <span>{financialStats.tier.label}</span>
                </div>
                <Progress value={financialStats.tier.value} className="h-2" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 text-sm">
                <MiniStat
                  icon={CreditCard}
                  label="Preferred Payment Method"
                  value={financialStats.paymentMethod}
                />
                <MiniStat
                  icon={DollarSign}
                  label="Avg Transaction Value"
                  value={
                    financialStats.avgTransaction
                      ? `$${financialStats.avgTransaction.toFixed(2)}`
                      : "Not enough data"
                  }
                />
                <MiniStat
                  icon={Calendar}
                  label="Purchase Frequency"
                  value={financialStats.frequency}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="social" className="space-y-5">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Social &amp; Influence Metrics</CardTitle>
              <CardDescription>
                Social capital and referral-based growth metrics
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col md:flex-row items-center md:items-start md:justify-between gap-6">
              <div className="grid grid-cols-2 gap-5 text-sm w-full md:w-1/2">
                <MiniStat icon={Users} label="Referrals" value={socialStats.referrals} />
                <MiniStat
                  icon={TrendingUp}
                  label="Conversion Rate"
                  value={`${socialStats.conversionRate}%`}
                />
                <MiniStat
                  icon={Network}
                  label="Followers"
                  value={
                    typeof socialStats.followers === "number"
                      ? socialStats.followers.toLocaleString()
                      : socialStats.followers
                  }
                />
                <MiniStat
                  icon={Award}
                  label="Influence Tier"
                  value={socialStats.influenceTier}
                />
              </div>
              <div className="flex gap-6">
                <CircularMetric
                  label="Influence Score"
                  value={socialStats.influenceScore}
                  color="#1e3a8a"
                />
                <CircularMetric
                  label="Referral Health"
                  value={clamp(socialStats.conversionRate * 1.5)}
                  color="#0f766e"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Content Affinity</CardTitle>
              <CardDescription>
                Preferred formats and topics with engagement bias
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">
                  Preferred Content Types
                </h4>
                {Array.isArray(contentAffinity.preferredTypes) &&
                contentAffinity.preferredTypes.length ? (
                  <div className="flex flex-wrap gap-2">
                    {contentAffinity.preferredTypes.map((item: string, idx: number) => (
                      <Badge key={idx} variant="outline" className="rounded-full">
                        {item}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">
                    No content preferences captured.
                  </p>
                )}
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">
                  Favourite Topics
                </h4>
                {Array.isArray(contentAffinity.topics) &&
                contentAffinity.topics.length ? (
                  <div className="flex flex-wrap gap-2">
                    {contentAffinity.topics.map((item: string, idx: number) => (
                      <Badge key={idx} className="rounded-full bg-indigo-600 text-white">
                        {item}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No topic affinity detected.</p>
                )}
                <div className="mt-3">
                  <p className="text-xs uppercase text-slate-500">Engagement Type</p>
                  <p className="text-sm font-medium text-slate-700">
                    {contentAffinity.engagementType || "Unknown"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insights" className="space-y-5">
          <Card className="shadow-sm">
            <CardHeader>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col gap-1 cursor-help">
                      <CardTitle>Intent &amp; Motivation</CardTitle>
                      <CardDescription>
                        Propensity scores derived from behavioural intent signals
                      </CardDescription>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Converts behavioural patterns into easy-to-scan intent
                    scores (for example deal-seeking or study interest).
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardHeader>
            <CardContent className="space-y-4">
              {intentScores.map((item, idx) => (
                <div key={idx}>
                  <div className="flex justify-between text-sm text-slate-700 mb-1">
                    <span>{item.label}</span>
                    <span>{item.value}%</span>
                  </div>
                  <Progress value={item.value} className="h-2" />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col gap-1 cursor-help">
                      <CardTitle>Sentiment &amp; Feedback</CardTitle>
                      <CardDescription>
                        Qualitative and quantitative feedback signals
                      </CardDescription>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Rolls up survey answers and other feedback into a single
                    sentiment score and narrative.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardHeader>
            <CardContent className="flex items-center gap-4">
              <div className="flex flex-col items-center justify-center h-20 w-20 rounded-full bg-emerald-50 text-emerald-600 font-semibold text-xl">
                {sentiment.score}/10
                <span className="text-xs text-emerald-700">
                  {sentiment.score >= 8
                    ? "Positive"
                    : sentiment.score >= 6
                      ? "Neutral"
                      : "Low"}
                </span>
              </div>
              <p className="text-sm text-slate-600">{sentiment.summary}</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai" className="space-y-5">
          <Card className="shadow-sm">
            <CardHeader>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col gap-1 cursor-help">
                      <CardTitle>AI Personality &amp; Traits</CardTitle>
                      <CardDescription>
                        Structured profile generated from behavioural and demographic signals
                      </CardDescription>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    AI-generated view of personality traits based on this
                    user&apos;s behaviour and profile.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {formattedLastAiUpdate && (
                <p className="mt-1 text-xs text-slate-400">
                  Last AI update {formattedLastAiUpdate}
                </p>
              )}
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {aiUserAnalysis ? (
                <>
                  <div className="space-y-2">
                    <p className="text-xs uppercase text-slate-500">
                      Personality Traits
                    </p>
                    {Array.isArray(
                      aiUserAnalysis.personalityProfile?.traits,
                    ) && aiUserAnalysis.personalityProfile.traits.length ? (
                      <div className="flex flex-wrap gap-2">
                        {aiUserAnalysis.personalityProfile.traits.map(
                          (trait: string, idx: number) => (
                            <Badge
                              key={idx}
                              variant="outline"
                              className="rounded-full"
                            >
                              {trait}
                            </Badge>
                          ),
                        )}
                      </div>
                    ) : (
                      <p className="text-slate-500">
                        No traits detected yet.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs uppercase text-slate-500">
                      Core Preferences
                    </p>
                    {Array.isArray(
                      aiUserAnalysis.personalityProfile?.preferences,
                    ) &&
                    aiUserAnalysis.personalityProfile.preferences.length ? (
                      <ul className="list-disc list-inside text-slate-700 space-y-1">
                        {aiUserAnalysis.personalityProfile.preferences.map(
                          (pref: string, idx: number) => (
                            <li key={idx}>{pref}</li>
                          ),
                        )}
                      </ul>
                    ) : (
                      <p className="text-slate-500">
                        Preferences will appear after running AI analysis.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs uppercase text-slate-500">
                      AI Insights &amp; Segments
                    </p>
                    {Array.isArray(aiUserAnalysis.insights) &&
                    aiUserAnalysis.insights.length ? (
                      <ul className="list-disc list-inside text-slate-700 space-y-1 mb-2">
                        {aiUserAnalysis.insights.map(
                          (insight: string, idx: number) => (
                            <li key={idx}>{insight}</li>
                          ),
                        )}
                      </ul>
                    ) : (
                      <p className="text-slate-500">
                        Insights will be generated once AI analysis has run.
                      </p>
                    )}
                    {Array.isArray(aiUserAnalysis.recommendedSegments) &&
                      aiUserAnalysis.recommendedSegments.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs uppercase text-slate-500 mb-1">
                            Recommended Segments
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {aiUserAnalysis.recommendedSegments.map(
                              (seg: string, idx: number) => (
                                <Badge
                                  key={idx}
                                  className="rounded-full bg-indigo-600 text-white"
                                >
                                  {seg}
                                </Badge>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    {typeof aiUserAnalysis.confidence === "number" && (
                      <p className="mt-3 text-xs text-slate-500">
                        Confidence score:{" "}
                        {Math.round(aiUserAnalysis.confidence * 100)}%
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="col-span-1 md:col-span-3 text-sm text-slate-600">
                  No AI personality profile has been generated yet. Use the{" "}
                  <span className="font-medium">Analyze with AI</span> button
                  above to run a fresh analysis for this user.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Predictive &amp; AI Insights</CardTitle>
              <CardDescription>
                Machine learning predictions aligned to engagement objectives
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div>
                  <div className="flex justify-between text-sm text-slate-700 mb-1">
                    <span>Propensity to Bet</span>
                    <span>{Math.round(aiInsights.propensityToBet * 100)}%</span>
                  </div>
                  <Progress value={aiInsights.propensityToBet * 100} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm text-slate-700 mb-1">
                    <span>Propensity to Study</span>
                    <span>{Math.round(aiInsights.propensityToStudy * 100)}%</span>
                  </div>
                  <Progress value={aiInsights.propensityToStudy * 100} className="h-2" />
                </div>
                <div>
                  <p className="text-xs uppercase text-slate-500 mb-1">Cluster ID</p>
                  <Badge className="bg-slate-900 text-white rounded-full">
                    {aiInsights.clusterId}
                  </Badge>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 via-indigo-50 to-white border border-blue-100">
                <div className="flex items-center gap-2 text-sm font-semibold text-blue-900 mb-2">
                  <Sparkles className="h-4 w-4" />
                  AI Engagement Score
                </div>
                <div className="text-3xl font-bold text-blue-900">
                  {Math.round(aiInsights.engagementScore)}
                </div>
                <p className="text-xs text-blue-700 mt-2">
                  Generated from multi-channel behavioural clustering across
                  competitions, referrals, and sentiment inputs.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col gap-1 cursor-help">
                      <CardTitle>Trust &amp; Compliance</CardTitle>
                      <CardDescription>
                        Verification and data governance checkpoints
                      </CardDescription>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Summarises verification, KYC, consent, and
                    data-retention state for this user.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <ComplianceBadge
                label="Verification Status"
                status={profile?.isVerified ? "Verified" : "Pending"}
              />
              <ComplianceBadge
                label="KYC"
                status={profile?.kycStatus || "Pending Verification"}
              />
              <ComplianceBadge
                label="Consent"
                status={profile?.marketingConsent ? "Verified" : "Opted Out"}
              />
              <ComplianceBadge
                label="Data Retention"
                status={profile?.dataRetention || "Active"}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="data" className="space-y-5">
          <Card className="shadow-sm">
            <CardHeader>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col gap-1 cursor-help">
                      <CardTitle>Data Sources</CardTitle>
                      <CardDescription>
                        Source catalog and update cadences powering this profile
                      </CardDescription>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Lists where this profile&apos;s data originates and how
                    frequently each source is refreshed.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardHeader>
            <CardContent className="overflow-hidden rounded-lg border border-slate-100">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Source</th>
                    <th className="px-4 py-3 text-left font-medium">Type</th>
                    <th className="px-4 py-3 text-left font-medium">
                      Update Frequency
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {dataSources.map((source: any, idx: number) => (
                    <tr key={idx}>
                      <td className="px-4 py-3 text-slate-700">
                        {source?.source || "Unknown"}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="rounded-full">
                          {source?.type || "N/A"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {source?.frequency || "Unknown"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
